import { GridStatesService } from 'src/app/_services/grid-states.service';
import { Subscription } from 'rxjs';
import {
  Component, EventEmitter, Output, Input, ViewChildren, QueryList, ContentChildren,
  ContentChild, TemplateRef, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { GridColumnModel } from 'src/app/_models/grid-column.model';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDragEnd } from '@angular/cdk/drag-drop';
import { Datacolumn } from './datacolumn/datacolumn.component';
import { SortableHeader } from './directives/sortableHeader.directive';
import { SubRowTemplateDirective } from './directives/subRowTemplate.directive';
import _ from 'lodash';
import { MatCheckboxChange } from '@angular/material/checkbox';

export type SortDirection = 'asc' | 'desc' | '';

const STRING_FILTER_TYPES = [undefined, 'title', 'template'];
const NUMBER_FILTER_TYPES = ['number', 'currency', 'percent', 'date'];

export interface SortEvent {
  column: string;
  direction: SortDirection;
}

@Component({
  selector: 'datatable',
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.scss'],
})
export class DatatableComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() set items(items: any[]) {
    this._items = items || [];
    this.expandedRowsHeight = 0;
    this.expandedRows = [];
    this.subRowHeights = {};
  }

  private _pendingGridColumns: GridColumnModel[] | null = null;
  private _applyQueued: boolean = false;
  private _lastAppliedSignature: string | null = null;
  @Input() set gridColumns(gridColumns: GridColumnModel[]) {
    if (!gridColumns) return;
    // Always stage and schedule application to coalesce rapid updates
    this._pendingGridColumns = gridColumns;
    this.queueApplyGridColumns();
  }

  /** Stable page/table id, used only when the page has no named-view controller. */
  @Input() gridStateKey: string;
  private layoutSubscriptions = new Subscription();
  private layoutEdited = false;
  private mouseMove = (event: MouseEvent) => this.resizerMouseMove(event);
  private mouseUp = (event: MouseEvent) => this.resizerMouseUp(event);
  private windowResize = () => this.refreshAllSubRows();

  @Input() parentKey: number;
  @Input() key = 'id';
  @Input() pageSize = 20;
  @Input() sort: string;
  @Input() sortField: string;
  @Input() groupingField: string;
  @Input() total: number;
  @Input() page = 1;
  @Input() loading = true;
  @Input() editing = false;
  @Input() isExpandable = false;
  @Input() subsCountField: string;
  @Input() selectable = true;
  @Input() showRowNumber: boolean = false;
  @Input() columnResizable = true;
  @Input() showFilters = true;
  @Input() columnsChooser = true;
  @Input() columnsSorting = true;
  @Input() expandedColumn: string;
  @Input() sorting = true;
  @Input() formGroup: UntypedFormGroup;
  @Input() assignedTo: any;
  @Input() headerStickyMargin: number = 80;
  @Input() headerHeight: number;
  @Input() rowHeight: number;
  @Input() maxVisibleRows: number = 20;
  @Input() minBufferPx: number = 1600;
  @Input() maxBufferPx: number = 10000;
  @Input() border = true;
  @Input() selectedItems: any[] = [];
  @Input() disableSelect: any[] = [];
  @Input() showSelectAllHeader: boolean = false;
  @Output() selectedChanged: EventEmitter<any[]> = new EventEmitter();
  @Output() loadRequested: EventEmitter<any> = new EventEmitter();
  @Output() filtersChanged: EventEmitter<[]> = new EventEmitter();
  @Output() columnsChanged: EventEmitter<GridColumnModel[]> = new EventEmitter();
  @Output() rowClick: EventEmitter<any> = new EventEmitter();
  @Output() columnsExpand: EventEmitter<any> = new EventEmitter();
  @ViewChildren(SortableHeader) headers: QueryList<SortableHeader>;
  @ContentChildren(Datacolumn, { descendants: false, read: Datacolumn }) set columns(columns: QueryList<Datacolumn>) {
    this._columns = _.clone(columns);
    this._columns.forEach((col, i) => col.columnIndex = i);
    this.updateWidth();
    // Apply any pending grid-state once columns are ready
    if (this._pendingGridColumns) this.queueApplyGridColumns();
    // Re-apply on any future structural changes to projected columns
    this._columns?.changes?.subscribe(() => {
      this._columns.forEach((col, i) => col.columnIndex = i);
      if (this._pendingGridColumns) this.queueApplyGridColumns();
    });
  };

  private queueApplyGridColumns() {
    if (!this._columns || !this._pendingGridColumns) return;
    if (this._applyQueued) return;
    this._applyQueued = true;
    const run = () => {
      this._applyQueued = false;
      const pending = this._pendingGridColumns;
      if (!pending) return;
      this.applyGridColumnsModels(pending);
      // do not clear _pendingGridColumns so a later call can re-apply latest order if needed
    };
    // Defer to next macrotask to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(run, 0);
  }

  private applyGridColumnsModels(gridColumns: GridColumnModel[]) {
    if (!this._columns) return;
    const used = new Set<Datacolumn>();
    const allCols = this._columns.toArray();
    const makeSig = (cols: Datacolumn[]) => cols.map(c => `${c.freezed || 'c'}|${c.name || (c.columns||[]).join(',')}|${Number(c.width)}|${String(c.visible)}`).join('||');
    const currentSig = makeSig(allCols);
    const left = allCols.filter(c => c.hasPermission && c.freezed === 'left');
    const right = allCols.filter(c => c.hasPermission && c.freezed === 'right');
    const center = allCols.filter(c => c.hasPermission && !c.freezed);


    const findMatch = (gCol: GridColumnModel) => {
      const byKey = (cols: Datacolumn[]) => cols.find(col => {
        const nameMatch = gCol.name && col.name && col.name === gCol.name;
        const groupMatch = Array.isArray(gCol.columns) && gCol.columns.length > 0 && Array.isArray(col.columns)
          && gCol.columns.length === col.columns.length
          && gCol.columns.every((v, i) => v === col.columns[i]);
        return (nameMatch || groupMatch) && !used.has(col);
      });
      const region = gCol.freezed === 'left' ? left : gCol.freezed === 'right' ? right : center;
      return byKey(region) || byKey(allCols);
    };

    const ordered: Datacolumn[] = [];
    for (let gCol of gridColumns) {
      const match = findMatch(gCol);
      if (match) {
        if (typeof gCol.visible === 'boolean') match.visible = gCol.visible;
        if (Number.isFinite(gCol.width) && gCol.width > 0) match.width = gCol.width;
        used.add(match);
        ordered.push(match);
      }
    }
    // Append any remaining columns preserving their natural order and keep their current visibility
    for (let col of allCols) {
      if (!used.has(col)) ordered.push(col);
    }
    // Compare against the pre-apply signature so width/visibility-only changes repaint.
    const nextSig = makeSig(ordered);
    if (currentSig === nextSig) {
      this._lastAppliedSignature = nextSig;
      return;
    }
    // Apply new order
    this._columns.reset(ordered);
    this.updateWidth();
    this._lastAppliedSignature = nextSig;
  }
  @ContentChild(SubRowTemplateDirective, { read: TemplateRef }) subRowTemplate: TemplateRef<any>;
  @ViewChild('datatable') datatable: ElementRef;
  @ViewChild('tableHeader') tableHeader: ElementRef;
  @ViewChild('expandedColumnHeader') expandedColumnHeader: ElementRef;

  _columns: QueryList<Datacolumn>;
  math = Math;
  filters = {};
  _items: any[] = [];
  highlightedRow: number;
  pageX: number;
  resizedCol: Datacolumn;
  curCol: HTMLElement;
  curColWidth: number;
  columnsExpanded: boolean = true;
  lastGroupValue: any;
  subRowHeights: any = {};
  expandedRows: number[] = [];
  avgRowHeight: number;
  rightWidth = 0;
  leftWidth = 0;
  expandedRowsHeight = 0;
  @Input() paginationOptions: number[] = [10, 20, 50, 100, 500]

  constructor(private formBuilder: UntypedFormBuilder, private gridStates: GridStatesService) { }

  ngAfterViewInit() {
    setTimeout(() => this.updateWidth(), 1000);
  }

  ngOnInit() {
    if (this.gridStateKey) {
      this.layoutSubscriptions.add(this.gridStates.get(this.gridStateKey).subscribe({
        next: state => {
          if (state && !this.layoutEdited) this.gridColumns = Array.isArray(state) ? state : state.columns;
        }, error: () => {}
      }));
      this.layoutSubscriptions.add(this.columnsChanged.subscribe(columns => {
        this.layoutEdited = true;
        this._pendingGridColumns = columns;
        // Writes are captured immediately and serialized by the shared service.
        this.gridStates.saveColumns(this.gridStateKey, columns).subscribe({ error: () => {} });
      }));
    }
    if (this.formGroup == null) {
      this.formGroup = this.formBuilder.group({});
    }
    if (this.assignedTo) {
      this.assignedTo.datatable = this;
    }
    if (this.columnResizable) {
      document.addEventListener('mousemove', this.mouseMove);
      document.addEventListener('mouseup', this.mouseUp);
      window.addEventListener('resize', this.windowResize);
    }
    this.avgRowHeight = this.rowHeight || 100;
  }

  ngOnDestroy() {
    this.layoutSubscriptions.unsubscribe();
    document.removeEventListener('mousemove', this.mouseMove);
    document.removeEventListener('mouseup', this.mouseUp);
    window.removeEventListener('resize', this.windowResize);
  }

  refreshAllSubRows() {
    for (let i of this.expandedRows)
      this.refreshSubRows(i);
  }

  refreshSubRows(index) {
    const leftSubRow = this.datatable.nativeElement.querySelector(`#subsLeft${index}`) as HTMLElement;
    const datatable = this.datatable.nativeElement as HTMLElement;
    const contentSubRow = this.datatable.nativeElement.querySelectorAll(`#subs${index}, #subsRight${index}`) as NodeListOf<HTMLElement>;
    const leftContent = this.datatable.nativeElement.querySelector('.datatable-left.content') as HTMLElement;
    if (leftSubRow) {
      leftSubRow.style.marginRight = `-${datatable?.offsetWidth - leftContent?.offsetWidth}px`;
      leftSubRow.style.width = `${datatable?.offsetWidth}px`;
      setTimeout(() => contentSubRow?.forEach(r => r.style.height = `${leftSubRow.offsetHeight}px`), 1);
    }
  }

  getGroupValue(item: any) {
    this.lastGroupValue = item[this.groupingField];
    return item[this.groupingField];
  }

  sum(position: string, field: string) {
    return this._columns.filter((c) => c.hasPermission && c.visible && ((position == 'center' && !c.freezed) || c.freezed == position) && !c.subDatatableColumn)
      .reduce((sum, current) => sum + (current.subColumns.reduce((subSum, subCurrent) => subSum + subCurrent.width, 0) || current.width), 0);
  }

  setSubRowHeight(item: any, height: number) {
    this.subRowHeights[item] = height;
    return height;
  }

  getHeaderHeight() {
    if (this.datatable) {
      (<HTMLElement>this.datatable.nativeElement.querySelector(`.datatable-center > .datatable-row`)).style.height = 'auto';
      (<HTMLElement>this.datatable.nativeElement.querySelector(`.datatable-left > .datatable-row`)).style.height = 'auto';
      (<HTMLElement>this.datatable.nativeElement.querySelector(`.datatable-right > .datatable-row`)).style.height = 'auto';
      let height = (<HTMLElement>this.datatable.nativeElement.querySelector(`.datatable-center > .datatable-row`)).offsetHeight;
      let height2 = (<HTMLElement>this.datatable.nativeElement.querySelector(`.datatable-left > .datatable-row`)).offsetHeight;
      if (height2 > height)
        height = height2;
      height2 = (<HTMLElement>this.datatable.nativeElement.querySelector(`.datatable-right > .datatable-row`)).offsetHeight;
      if (height2 > height)
        height = height2;
      (<HTMLElement>this.datatable.nativeElement.querySelector(`.datatable-center > .datatable-row`)).style.height = `${height}px`;
      (<HTMLElement>this.datatable.nativeElement.querySelector(`.datatable-left > .datatable-row`)).style.height = `${height}px`;
      (<HTMLElement>this.datatable.nativeElement.querySelector(`.datatable-right > .datatable-row`)).style.height = `${height}px`;
    }
  }

  getGridColumns(includeTemplate: boolean = true) {
    const gridCols: GridColumnModel[] = [];
    this._columns.forEach((datacol) => {
      const col = new GridColumnModel();
      col.columnIndex = datacol.columnIndex;
      col.permissions = datacol.permissions;
      col.name = datacol.name;
      col.width = datacol.width;
      col.visible = String(datacol.visible) !== 'false';
      if (includeTemplate)
        col.titleTemplate = datacol.titleTemplate;
      col.freezed = datacol.freezed;
      col.columns = datacol.columns;
      gridCols.push(col);
    });
    return gridCols;
  }

  pageChanged(e: any) {
    this.loadRequested.emit({
      parentKey: this.parentKey, page: this.page, pageSize: this.pageSize,
      sort: this.sort, sortField: this.sortField, filters: this.filters
    });
  }

  onSort({ column, direction }: SortEvent) {
    // resetting other headers
    this.headers.forEach(header => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    this.loadRequested.emit({
      parentKey: this.parentKey, page: this.page, pageSize: this.pageSize,
      sort: direction, sortField: (column && direction ? column : ''), filters: this.filters
    });
  }

  filter(e: any, col: Datacolumn) {
    if (!col.filterType) {
      if (col.type === 'currency' || col.type === 'number' || col.type === 'percent' || col.type === 'date')
        col.filterType = 'Equal';
      else
        col.filterType = 'Contains';
    }
    if (e.target.value)
      this.filters[col.name] = { type: col.filterType, dataType: col.type, value: e.target.value };
    else
      delete this.filters[col.name];
    this.loadRequested.emit({
      parentKey: this.parentKey, page: this.page, pageSize: this.pageSize,
      sort: this.sort, sortField: this.sortField, filters: this.filters
    });
  }

  selectAll(e: MatCheckboxChange) {
    // Toggle only this page's rows; selections kept from other pages must survive.
    const pageItems = [];
    this._items.forEach(item => {
      pageItems.push(item);
      if (item.subItems && Array.isArray(item.subItems.items)) pageItems.push(...item.subItems.items);
    });
    const kept = this.selectedItems.filter(si => !pageItems.some(item => this.sameKey(si, item)));
    this.selectedItems.splice(0, this.selectedItems.length, ...kept);
    if (e.checked) {
      this._items.forEach(item => {
        if (!this.disableSelect.includes(item)) {
          this.selectedItems.push(item);
          if (item.subItems && Array.isArray(item.subItems.items)) {
            item.subItems.items.forEach(subItem => {
              if (!this.disableSelect.includes(subItem))
                this.selectedItems.push(subItem);
            });
          }
        }
      });
    }
    this.selectedItems = [...this.selectedItems];
    this.selectedChanged.emit(this.selectedItems);
  }

  isSelected(item: any) {
    return this.selectedItems.some(si => this.sameKey(si, item));
  }

  private sameKey(a: any, b: any) {
    return this.key.split(',').every(k => a[k.trim()] == b[k.trim()]);
  }

  select(e: MatCheckboxChange, data) {
    if (e.checked) {
      this.selectedItems.push(data);
    } else {
      // Match by key: a row reloaded on page change is a new object, so indexOf would miss it.
      const index = this.selectedItems.findIndex(si => this.sameKey(si, data));
      if (index !== -1) this.selectedItems.splice(index, 1);
    }
    this.selectedItems = [...this.selectedItems];
    this.selectedChanged.emit(this.selectedItems);
  }

  expand(e: any, item: any, index) {
    const expandedIndex = Number(e.currentTarget?.getAttribute('data-index') ?? index);
    if (!this.expandedRows.includes(expandedIndex)) {
      this.expandedRows.push(expandedIndex);
      const callback = this.subRowTemplate ? () => this.refreshSubRows(expandedIndex) : () => { };
      setTimeout(() => {
        const el = document.getElementById('subsLeft' + index);
        this.expandedRowsHeight += el.offsetHeight;
      }, 500);
      if (item.subItems === undefined) {
        let parentKey = this.key.split(',').map(k => item[k.trim()]);
        if (parentKey.length === 1) {
          parentKey = parentKey[0];
        }
        this.loadRequested.emit({
          parentKey: parentKey, page: this.page, pageSize: this.pageSize,
          sort: this.sort, sortField: this.sortField, filters: {}, callback: callback
        });
      } else {
        callback();
      }
    } else {
      const el = document.getElementById('subsLeft' + index);
      if ((this.expandedRowsHeight - el.offsetHeight) > 0) {
        this.expandedRowsHeight -= el.offsetHeight;
      } else {
        this.expandedRowsHeight = 0;
      }
      this.expandedRows = this.expandedRows.filter(i => i != expandedIndex);
    }
    this.expandedRows = [...this.expandedRows];
    return false;
  }

  updateWidth() {
    this.rightWidth = this._columns.filter(col => col.hasPermission && col.visible === true && col.freezed === 'right').reduce((a, b) => a + Number(b.width), 0);
    this.leftWidth = this._columns.filter(col => col.hasPermission && col.visible === true && col.freezed === 'left').reduce((a, b) => a + Number(b.width), 0);
  }

  valueChanged(e: any, item: any, col: Datacolumn) {
    switch (col.type) {
      case 'percent':
        item[col.name] = parseFloat(e.target.value) / 100;
        col.change(item[col.name], item);
        break;
      case 'lookup':
        item[col.name] = col.bindValue && e ? e[col.bindValue] : e;
        col.change(e, item);
        break;
      default:
        item[col.name] = e.target.value;
        col.change(item[col.name], item);
        break;
    }
  }

  openLookup(index: number) {
    setTimeout(() => {
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const selectPosition = this.datatable.nativeElement.querySelectorAll('ng-select[appendto="#external"]')[index].getBoundingClientRect();
      const panelStyle = (this.datatable.nativeElement.getElementsByTagName('ng-dropdown-panel')[0] as HTMLElement).style;
      panelStyle.left = `${selectPosition.left + scrollLeft}px`;
      panelStyle.top = `${selectPosition.top + scrollTop + selectPosition.height}px`;
    }, 1);
  }

  rowClicked(e: any, data: any, col: Datacolumn) {
    if (e.target.tagName != 'A' && e.target.tagName != 'BUTTON' && (!e.target.parentElement ||
      (e.target.parentElement.tagName != 'A' && e.target.parentElement.tagName != 'BUTTON')))
      this.rowClick.emit({ event: e, data: data, column: col });
  }

  isValid() {
    if (this.editing) {
      return false;
    }
  }

  resizerMouseDown(e: any, col: Datacolumn) {
    this.resizedCol = col;
    this.curCol = e.target.parentElement;
    this.pageX = e.pageX;
    this.curColWidth = this.curCol.offsetWidth;
    this.headers.forEach(header => header.resizing = true);
  }

  resizerMouseMove(e: any) {
    if (this.curCol) {
      const diffX = e.pageX - this.pageX;
      const newWidth = this.curColWidth + diffX;
      if (newWidth >= 30) {
        this.resizedCol.width = newWidth;
        const colClass = this.curCol.className.split(' ').find(i => i.startsWith('col'));
        (this.datatable.nativeElement.querySelectorAll(`.${colClass}`) as NodeListOf<HTMLElement>)
          .forEach(col => col.style.width = newWidth + 'px');

        this.updateWidth();
      }
    }
  }

  resizerMouseUp(e: any) {
    setTimeout(() => this.headers.forEach(header => header.resizing = false), 1);
    if (this.resizedCol) {
      this.columnsChanged.emit(this.getGridColumns());
    }
    this.resizedCol = undefined;
    this.curCol = undefined;
    this.pageX = undefined;
    this.curColWidth = undefined;
  }

  dragEnded(e: CdkDragEnd<any>) {
    e.source.element.nativeElement.style.removeProperty('transform');
  }

  drop(event: CdkDragDrop<Datacolumn[]>, visible: boolean = true) {
    if (event.container === event.previousContainer) {
      const columns = this._columns.toArray();
      moveItemInArray(columns, event.previousIndex, event.currentIndex);
      this._columns.reset(columns);
    } else {
      event.previousContainer.data[event.previousIndex].visible = visible;
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
    this.columnsChanged.emit(this.getGridColumns());
  }

  showStringFilter(colType: string) {
    return STRING_FILTER_TYPES.includes(colType);
  }

  showNumberFilter(colType: string) {
    return NUMBER_FILTER_TYPES.includes(colType);
  }

  changeFilterType(e: any, col: Datacolumn) {
    const filterType = e.target.getAttribute('data-type');
    e.target.parentElement.parentElement.children[0].children[0].className = e.target.children[0].className;
    col.filterType = filterType;
    const filter = this.filters[col.name];
    if (filter) {
      filter.type = filterType;
      this.loadRequested.emit({
        parentKey: this.parentKey, page: this.page, pageSize: this.pageSize,
        sort: this.sort, sortField: this.sortField, filters: this.filters
      });
    }
  }

  scroll(e) {
    this.tableHeader.nativeElement.scrollTo({ left: e.srcElement.scrollLeft });
    this.expandedColumnHeader?.nativeElement?.scrollTo({ left: e.srcElement.scrollLeft });
  }

  expandColumns() {
    this.columnsExpanded = !this.columnsExpanded;
    this.columnsExpand.emit(this.columnsExpanded);
  }
}
